import React, { useState } from 'react';
import { PhoneIcon, CheckIcon, ThumbsUpIcon, MessageCircleIcon, Share2Icon, PaperclipIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { hasContent } from '../RichTextEditor';
import { resolveHtml, resolveText } from '../../utils/variables';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
export type PreviewTab = 'push' | 'sms' | 'email' | 'voice' | 'website' | 'social';
interface NoticePreviewProps {
  channels: ChannelsState;
  title: string;
  description: string;
  categoryLabel: string;
  previewTab: PreviewTab;
  onPreviewTabChange: (tab: PreviewTab) => void;
  /** Variables used to resolve chips into real values. */
  variables: NoticeVariable[];
  /** Language the notice content is written in. */
  contentLang: 'fr' | 'en';
}
export function NoticePreview({
  channels,
  title,
  description,
  categoryLabel,
  previewTab,
  onPreviewTabChange,
  variables,
  contentLang
}: NoticePreviewProps) {
  const {
    t
  } = useLanguage();
  const [voiceTestPhone, setVoiceTestPhone] = useState('');
  const [voiceTestSent, setVoiceTestSent] = useState(false);

  /** Plain text with every variable replaced by its value. */
  const text = (html: string) => resolveText(html, variables, contentLang);
  /** Rich HTML with every variable replaced by its value. */
  const rich = (html: string) => resolveHtml(html, variables, contentLang);
  const tabs: {
    id: PreviewTab;
    label: string;
  }[] = [{
    id: 'push',
    label: t('Push', 'Push')
  }, {
    id: 'sms',
    label: t('SMS', 'SMS')
  }, {
    id: 'email',
    label: t('Courriel', 'Email')
  }, {
    id: 'voice',
    label: t('Voix', 'Voice')
  }, {
    id: 'website',
    label: t('Site web', 'Website')
  }, {
    id: 'social',
    label: t('Social', 'Social')
  }];
  const isPhone = previewTab === 'push' || previewTab === 'sms' || previewTab === 'voice';
  return <section className="space-y-4" data-id="element-3897" data-mp="eh0z07rx2rxs">
      <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-3898" data-mp="389iteb2kcuyw">
        <h2 className="text-base font-semibold text-slate-900" data-id="element-3899" data-mp="38bmwiqsn1py4">
          {t('Aperçu', 'Preview')}
        </h2>
        <div className="flex flex-wrap gap-1 bg-slate-200/50 p-1 rounded-lg" data-id="element-3900" data-mp="3dagb6mjnvmzv">
          {tabs.map(tab => <button key={tab.id} onClick={() => {
          onPreviewTabChange(tab.id);
          setVoiceTestSent(false);
        }} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${previewTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3901" data-mp="3m9bfft35an0j">
              {tab.label}
            </button>)}
        </div>
      </div>

      <div className="flex justify-center" data-id="element-3902" data-mp="25u0id15d0nv5">
        {/* Phone mockup — push / sms / voice */}
        {isPhone && <div className="w-[300px] h-[600px] bg-white rounded-[40px] border-[8px] border-slate-900 overflow-hidden relative" data-id="element-3903" data-mp="2ask3izc4pvmg">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-3904" data-mp="2389jv3uflegq">
              <div className="w-32 h-6 bg-slate-900 rounded-b-xl" data-id="element-3905" data-mp="1ycc5h32qai6l" />
            </div>

            <div className="w-full h-full bg-slate-50 pt-12 px-4 relative" data-id="element-3906" data-mp="1tav3t5cf6roy">
              {previewTab === 'push' && <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-100 mt-4" data-id="element-3907" data-mp="1pcxcnwz39vix">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3908" data-mp="1kdku3of93am5">
                    <img src="https://cdn.magicpatterns.com/uploads/ckK2136PdZbnMQPqZiNST7/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-5 h-5 rounded object-cover flex-shrink-0" data-id="element-3909" data-mp="1fjgksyiiiqhw" />
                    <span className="text-xs font-medium text-slate-500 truncate" data-id="element-3910" data-mp="11kqo3jzlusu2">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto" data-id="element-3911" data-mp="2wphkv51hptz7">
                      {t('Maintenant', 'Now')}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-3912" data-mp="2myvbhskd9zoy">
                    {text(channels.push.title) || title || t('Titre de la notification', 'Notification title')}
                  </h4>
                  <p className="text-sm text-slate-600 leading-snug" data-id="element-3913" data-mp="2cyw5o3fc0nfs">
                    {text(channels.push.message) || description || t('Le message de votre notification apparaîtra ici…', 'Your notification message will appear here…')}
                  </p>
                </div>}

              {previewTab === 'sms' && <div className="mt-8 space-y-4" data-id="element-3914" data-mp="780pzbf9bkum">
                  <div className="flex justify-center" data-id="element-3915" data-mp="76pkn765y51e">
                    <span className="text-[10px] font-medium text-slate-400" data-id="element-3916" data-mp="hamlospahdve">
                      {t("Aujourd'hui 10:42", 'Today 10:42')}
                    </span>
                  </div>
                  <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm p-3 max-w-[85%] ml-auto" data-id="element-3917" data-mp="q49dy8uafyzu">
                    <p className="text-sm leading-snug" data-id="element-3918" data-mp="lcrdrn03a2fx">
                      {text(channels.sms.text) || t('Votre message texte apparaîtra ici. Il doit être court et direct.', 'Your text message will appear here. Keep it short and direct.')}
                    </p>
                  </div>
                </div>}

              {previewTab === 'voice' && <div className="absolute inset-0 bg-[#0F172A] pt-14 px-5 flex flex-col" data-id="element-3919" data-mp="3atowyhn9mjex">
                  <div className="flex flex-col items-center text-center" data-id="element-3920" data-mp="36sd7cc4e5y6q">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-4" data-id="element-3921" data-mp="36ug6blniv1nl">
                      {t('Appel entrant', 'Incoming call')}
                    </span>
                    <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mb-3" data-id="element-3922" data-mp="1er2caskg1zr2">
                      <PhoneIcon className="w-8 h-8 text-[#4F46E5]" data-id="element-3923" data-mp="1jrzuh55getsk" />
                    </div>
                    <h4 className="text-base font-semibold text-white" data-id="element-3924" data-mp="1omwz7zvpagzn">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </h4>
                    <p className="text-xs text-slate-400 mb-4" data-id="element-3925" data-mp="1xomrt91bjlz0">
                      {t('Message vocal automatisé', 'Automated voice message')}
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-2xl p-3 text-left mb-4 overflow-y-auto max-h-32" data-id="element-3926" data-mp="27fr8qeak1gwh">
                    <p className="text-xs text-slate-200 leading-relaxed" data-id="element-3927" data-mp="2cfluy4jdxib7">
                      {text(channels.voice.script) || t('Le script de votre appel vocal sera lu ici par une voix automatisée…', 'Your call script will be read here by an automated voice…')}
                    </p>
                  </div>

                  <div className="mt-auto bg-white rounded-2xl p-4 mb-4" data-id="element-3928" data-mp="3ry1zarydq987">
                    {voiceTestSent ? <div className="text-center" data-id="element-3929" data-mp="3n09zlbrhtitg">
                        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2" data-id="element-3930" data-mp="oh2z98ejma83">
                          <CheckIcon className="w-5 h-5 text-emerald-600" data-id="element-3931" data-mp="jj1kgzo5gx2f" />
                        </div>
                        <p className="text-sm font-semibold text-slate-900" data-id="element-3932" data-mp="epffzus53zhp">
                          {t('Test envoyé !', 'Test sent!')}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3933" data-mp="177kq17ftevqt">
                          {t('Un appel test est en route vers ', 'A test call is on its way to ')}
                          {voiceTestPhone}.
                        </p>
                        <button onClick={() => setVoiceTestSent(false)} className="mt-2 text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA]" data-id="element-3934" data-mp="fkhyzil25pjr">
                          {t('Envoyer un autre test', 'Send another test')}
                        </button>
                      </div> : <>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5" data-id="element-3935" data-mp="yi2ehz1xnbrp">
                          {t("Tester l'appel vocal", 'Test the voice call')}
                        </label>
                        <input type="tel" value={voiceTestPhone} onChange={e => setVoiceTestPhone(e.target.value)} placeholder="(514) 555-0199" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] mb-2" data-id="element-3936" data-mp="pg2tywrplpk0" />
                        <button onClick={() => {
                  if (voiceTestPhone.trim()) setVoiceTestSent(true);
                }} disabled={!voiceTestPhone.trim()} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3937" data-mp="3bptv53pwn8wh">
                          <PhoneIcon className="w-4 h-4" data-id="element-3938" data-mp="2nzg0d5fxqvy7" />{' '}
                          {t('Envoyer un test', 'Send a test')}
                        </button>
                      </>}
                  </div>
                </div>}
            </div>
          </div>}

        {/* Social post mockup */}
        {previewTab === 'social' && <div className="w-full max-w-[360px] bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-3939" data-mp="hnv9sk0pcxjz">
            <div className="flex items-center gap-3 p-3 border-b border-slate-100" data-id="element-3940" data-mp="cnpn7i9ai18r">
              <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-3941" data-mp="7pxljwiuf0rd" />
              <div className="min-w-0" data-id="element-3942" data-mp="3v0nkfhep6fz8">
                <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-3943" data-mp="3q4gli8kpy4qs">
                  {t('Ville de Windsor', 'City of Windsor')}
                </p>
                <p className="text-[11px] text-slate-400" data-id="element-3944" data-mp="3g57v1pp2b896">
                  {t("À l'instant · ", 'Just now · ')}
                  <span aria-hidden="true" data-id="element-3945" data-mp="3c3w49gugcgze">🌐</span>
                </p>
              </div>
            </div>

            <div className="px-3 py-2" data-id="element-3946" data-mp="1rm4rc7p8vqvw">
              {hasContent(channels.social.postText) ? <div className="text-sm text-slate-700 leading-snug [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-2" dangerouslySetInnerHTML={{
            __html: rich(channels.social.postText)
          }} data-id="element-3947" data-mp="1vmnj61r50wy3" /> : <p className="text-sm text-slate-400" data-id="element-3948" data-mp="17vrlwp3wx9jg">
                  {t('Le texte de votre publication apparaîtra ici.', 'Your post text will appear here.')}
                </p>}
            </div>

            <div className="flex items-center justify-around px-3 py-2 border-t border-slate-100 text-slate-500" data-id="element-3949" data-mp="1mnti9z6rxkeq">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3950" data-mp="p5a7zqnqoii5">
                <ThumbsUpIcon className="w-4 h-4" data-id="element-3951" data-mp="u4cz076vwo74" /> {t("J'aime", 'Like')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3952" data-mp="m3oykc5ztwep">
                <MessageCircleIcon className="w-4 h-4" data-id="element-3953" data-mp="i3esz0m4580d" /> {t('Commenter', 'Comment')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3954" data-mp="vutdxczkzja2">
                <Share2Icon className="w-4 h-4" data-id="element-3955" data-mp="qxjc0xv09f5c" /> {t('Partager', 'Share')}
              </span>
            </div>
          </div>}

        {/* Desktop mockup — email / website */}
        {(previewTab === 'email' || previewTab === 'website') && <div className="w-full max-w-[360px]" data-id="element-3956" data-mp="d4k015ufm32j">
            <div className="bg-white rounded-xl border border-slate-300 overflow-hidden shadow-sm" data-id="element-3957" data-mp="d6emwpp9k599">
              <div className="h-9 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 px-3" data-id="element-3958" data-mp="845r7ll0ap3g">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-3959" data-mp="1oi5nmmkn7wly" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-3960" data-mp="1jjueyf633s5b" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-3961" data-mp="31i0so8dn7ilw" />
                <div className="ml-2 flex-1 h-5 bg-white rounded border border-slate-200 text-[9px] text-slate-400 flex items-center px-2 truncate" data-id="element-3962" data-mp="31kvbxwkx6xdq">
                  {previewTab === 'email' ? 'mail.villedewindsor.qc.ca' : 'villedewindsor.qc.ca'}
                </div>
              </div>

              <div className="bg-white h-[440px] overflow-y-auto relative" data-id="element-3963" data-mp="3l5zuuuu55hfp">
                {previewTab === 'email' && <div className="bg-white" data-id="element-3964" data-mp="3p6tgtio0wm5y">
                    <div className="px-5 py-3 border-b border-slate-100" data-id="element-3965" data-mp="3p9xvj9z470av">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-3966" data-mp="3bgg9niiyhbho">
                        {t('De: Ville de Windsor', 'From: City of Windsor')}
                      </p>
                      <h4 className="text-sm font-semibold text-slate-900" data-id="element-3967" data-mp="7yycq64q1ow1">
                        {text(channels.email.subject) || title || t('Objet du courriel', 'Email subject')}
                      </h4>
                    </div>
                    <div className="p-6" data-id="element-3968" data-mp="2fawxwrq69y04">
                      <img src="https://cdn.magicpatterns.com/uploads/nedc6b9KaGTagFGJXbwvij/pasted-image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="h-8 w-auto object-contain mb-5" data-id="element-3969" data-mp="2f9vb5pzsnn2k" />

                      <h1 className="text-xl font-bold text-slate-900 mb-3" data-id="element-3970" data-mp="332v9zdxg3xeb">
                        {title || t("Titre de l'avis", 'Notice title')}
                      </h1>

                      {hasContent(channels.email.body) ? <div className="text-sm text-slate-600 leading-relaxed [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-3" dangerouslySetInnerHTML={{
                  __html: rich(channels.email.body)
                }} data-id="element-3971" data-mp="2p9vzxrgfmve5" /> : <p className="text-sm text-slate-400 leading-relaxed" data-id="element-3972" data-mp="3u7hgc0n17ri4">
                          {description || t("Le contenu détaillé de votre courriel s'affichera ici.", 'The detailed content of your email will appear here.')}
                        </p>}

                      {channels.email.attachments.length > 0 && <div className="mt-5 pt-4 border-t border-slate-100" data-id="element-3973" data-mp="lspmyomzqsz5">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2" data-id="element-3974" data-mp="lutoizxf3fw9">
                            {channels.email.attachments.length}{' '}
                            {t('pièce(s) jointe(s)', 'attachment(s)')}
                          </p>
                          <ul className="space-y-1.5" data-id="element-3975" data-mp="zsi7u3bu6ez7">
                            {channels.email.attachments.map(file => <li key={file.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5" data-id="element-3976" data-mp="2rts1upk4z20q">
                                <PaperclipIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-3977" data-mp="30u7xxdyjv6vk" />
                                <span className="text-xs text-slate-600 truncate" data-id="element-3978" data-mp="3fib7syfpr2pn">
                                  {file.name}
                                </span>
                              </li>)}
                          </ul>
                        </div>}
                    </div>
                  </div>}

                {previewTab === 'website' && <div className="relative" data-id="element-3979" data-mp="1u69sbcinhvvo">
                    <img src="https://cdn.magicpatterns.com/uploads/paDPkCcwNHTw4rcW21cM4q/image.png" alt={t('Aperçu du site web de Windsor', 'Preview of the Windsor website')} className="w-full block" data-id="element-3980" data-mp="1p76m3oqxpjm4" />
                    <div className="absolute top-[13%] inset-x-0 bg-[#E7F1FB] px-3 py-3 border-y border-[#cfe0f5]" data-id="element-3981" data-mp="3rzvrt4mc9fxz">
                      <div className="flex items-start justify-between gap-2" data-id="element-3982" data-mp="3w17bktij76y9">
                        <div className="min-w-0" data-id="element-3983" data-mp="3i5veifgwl4e6">
                          <span className="inline-block px-3 py-0.5 rounded-full bg-[#1D4ED8] text-white text-[10px] font-bold mb-1.5" data-id="element-3984" data-mp="3n35bqhajakck">
                            {categoryLabel}
                          </span>
                          <p className="text-[11px] font-bold text-slate-900 leading-snug" data-id="element-3985" data-mp="3d5qvahom1vk9">
                            {text(channels.website.bannerText) || title || t('Le texte de votre bannière apparaîtra ici', 'Your banner text will appear here')}
                          </p>
                          <p className="text-[10px] font-semibold text-slate-800 underline mt-1" data-id="element-3986" data-mp="3qcdb3ppcnz2g">
                            {text(channels.website.ctaLabel) || t("Consulter l'avis en cours", 'View the current notice')}
                          </p>
                        </div>
                        <span className="text-slate-500 text-sm leading-none" data-id="element-3987" data-mp="3cgj4xefynnt1">✕</span>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            <div className="mx-auto h-2.5 w-[88%] bg-slate-300 rounded-b-xl" data-id="element-3988" data-mp="2xluz1z6nencw" />
            <div className="mx-auto h-1 w-[40%] bg-slate-200 rounded-b-lg" data-id="element-3989" data-mp="2xnzd39y9h2qw" />
          </div>}
      </div>
    </section>;
}